/* SSi — Sales page for online 1:1 English teachers.
 *
 * One subscription that lets teachers (a) teach WITH SSi in their
 * own lessons, and (b) resell branded SSi subscriptions to their
 * students and keep the revenue.
 *
 * Rebuilt 2026-07-13 from Aran's redraft (gwefan/updates/tutors.md).
 * This file keeps the hero, the maths (TierLadder), sample+pricing,
 * FAQ and footer CTA; the narrative sections (results & evidence,
 * answer, mechanism, clean conscience, built-by, founding teachers,
 * first week) live in TeachersOnlineAudience.jsx.
 *
 * Reuses Header + Footer + the existing <ssi-lesson-player> Sample
 * widget and visual vocabulary from the main site — same fonts,
 * palette, buttons, spacing rhythm.
 */
import { useState, useRef, useEffect, Fragment } from 'react';
import { ensureLessonPlayer } from '../lessonPlayerLoader.js';

/* CTA destination — the app account-setup flow (NOT the consumer checkout).
 * The primary, pricing and footer CTAs all route here; the app branches
 * onboarding for the resell/teacher plan off the /tutors path. */
export const TEACHER_SETUP_URL = 'https://www.saysomethingin.app/tutors';

export function TeachersHero({ t }) {
  return (
    <section className="ssi-to__hero" style={thHeroStyles.wrap}>
      <div className="ssi-to__heroInner" style={thHeroStyles.inner}>
        <div className="ssi-to__heroCopy" style={thHeroStyles.copy}>
          <div style={thHeroStyles.kicker}>{t.toKicker}</div>
          <h1 style={thHeroStyles.h1}>{t.toHeadline}</h1>
          <p style={thHeroStyles.sub}>{t.toSub}</p>
          {t.toSubL1 && (
            <p style={thHeroStyles.sub}>
              {(() => {
                const marker = 'in their own language';
                const parts = (t.toSubL1 || '').split(marker);
                if (parts.length < 2) return t.toSubL1;
                return (
                  <>
                    {parts[0]}
                    <strong style={thHeroStyles.subAccent}>{marker}</strong>
                    {parts.slice(1).join(marker)}
                  </>
                );
              })()}
            </p>
          )}
          <div style={thHeroStyles.ctaRow}>
            <a href={TEACHER_SETUP_URL} className="btn btn--primary">{t.toCta}</a>
            <a href="#how" style={thHeroStyles.ghostLink}>{t.toCta2} →</a>
          </div>
          <p style={thHeroStyles.trust}>{t.toTrust}</p>
        </div>
        <div className="ssi-to__heroImage" style={thHeroStyles.image}>
          <div style={thHeroStyles.imgPlaceholder}>
            <img src="/assets/teacher-hero.jpg" alt="Online English teacher" style={thHeroStyles.img}/>
          </div>
          <div style={thHeroStyles.badge}>
            <div style={thHeroStyles.badgeBig}>£{t.toBadgeAmount}</div>
            <div style={thHeroStyles.badgeSmall}>{t.toBadgeLabel}</div>
          </div>
        </div>
      </div>
    </section>
  );
}

const thHeroStyles = {
  wrap: { background:'var(--bg-cream)', padding:'56px 32px 72px' },
  inner: { maxWidth:1160, margin:'0 auto', display:'grid', gridTemplateColumns:'1.05fr 1fr', gap:64, alignItems:'center' },
  copy: {},
  kicker: { fontSize:12, letterSpacing:'.16em', textTransform:'uppercase', color:'var(--brandRed)', fontWeight:700, marginBottom:18 },
  h1: { font:'700 64px/1.05 var(--font-display)', margin:'0 0 20px', color:'var(--fg-1)', letterSpacing:'-.01em' },
  sub: { font:'400 20px/1.5 var(--font-body)', color:'var(--fg-2)', margin:'0 0 20px', maxWidth:540 },
  subAccent: { color:'var(--brandRed)', fontWeight:700 },
  ctaRow: { display:'flex', alignItems:'center', gap:20, marginBottom:20 },
  ghostLink: { color:'var(--brandRed)', fontSize:15, fontWeight:600, textDecoration:'none' },
  trust: { fontSize:13, color:'var(--grey)', margin:0 },
  image: { position:'relative' },
  imgPlaceholder: {
    aspectRatio:'4/5', background:'#e9dccb',
    borderRadius:20, overflow:'hidden',
    boxShadow:'0 20px 60px -30px rgba(73,3,0,.35)',
  },
  img: { width:'100%', height:'100%', objectFit:'cover', display:'block', transform:'scaleX(-1)' },
  badge: {
    position:'absolute', right:-18, bottom:40, background:'#fff',
    borderRadius:16, padding:'16px 22px',
    boxShadow:'0 18px 40px -14px rgba(73,3,0,.35)',
    border:'1px solid var(--border)',
    minWidth:180,
  },
  badgeBig: { font:'700 34px/1 var(--font-display)', color:'var(--brandRed)' },
  badgeSmall: { fontSize:12, color:'var(--grey)', marginTop:6, lineHeight:1.4 },
};

/* -------------------------------------------------------------- */

export function TierLadder({ t }) {
  // The money-flow, three steps: your student pays £10 → you keep £5 →
  // your subscription is £15 (so break-even is 3 students). Middle card
  // highlighted, arrows between. Reuses the .ssi-to__tierGrid hook so the
  // existing mobile.css wrap/centre rules apply on small screens.
  const steps = [
    { label: t.toMathsStep1Label, big: t.toMathsStep1Big, sub: t.toMathsStep1Sub },
    { label: t.toMathsStep2Label, big: t.toMathsStep2Big, sub: t.toMathsStep2Sub, highlight: true },
    { label: t.toMathsStep3Label, big: t.toMathsStep3Big, sub: t.toMathsStep3Sub },
  ];

  return (
    <section id="maths" className="ssi-to__tier" style={tlStyles.wrap}>
      <div style={tlStyles.inner}>
        <div style={tlStyles.head}>
          <div style={tlStyles.kicker}>{t.toMathsKicker}</div>
          <h2 style={tlStyles.h2}>{t.toMathsTitle}</h2>
          <p style={tlStyles.lede}>{t.toMathsLede}</p>
          {t.toMathsTrial && <p style={tlStyles.trial}>{t.toMathsTrial}</p>}
        </div>

        <div className="ssi-to__tierGrid" style={tlStyles.steps}>
          {steps.map((s, i) => (
            <Fragment key={i}>
              {i > 0 && <div style={tlStyles.arrow} aria-hidden>→</div>}
              <div style={{...tlStyles.stepCard, ...(s.highlight ? tlStyles.stepHighlight : {})}}>
                <div style={tlStyles.stepLabel}>{s.label}</div>
                <div style={tlStyles.stepBig}>{s.big}</div>
                <div style={tlStyles.stepSub}>{s.sub}</div>
              </div>
            </Fragment>
          ))}
        </div>

        <p style={tlStyles.footnote}>{t.toMathsFootnote}</p>

        <div className="ssi-to__tierProof" style={tlStyles.proof}>
          <div style={tlStyles.proofStat}>{t.toMathsProofStat}</div>
          <div style={tlStyles.proofBody}>{t.toMathsProofBody}</div>
        </div>
      </div>
    </section>
  );
}

const tlStyles = {
  wrap: { background:'var(--bg-cream)', padding:'88px 32px' },
  inner: { maxWidth:1160, margin:'0 auto' },
  head: { textAlign:'center', maxWidth:720, margin:'0 auto 48px' },
  kicker: { fontSize:12, letterSpacing:'.16em', textTransform:'uppercase', color:'var(--brandRed)', fontWeight:700, marginBottom:14 },
  h2: { font:'700 48px/1.1 var(--font-display)', margin:'0 0 18px', color:'var(--fg-1)', letterSpacing:'-.01em' },
  lede: { font:'400 18px/1.55 var(--font-body)', color:'var(--fg-2)', margin:0 },
  trial: { font:'400 16px/1.55 var(--font-body)', color:'var(--fg-1)', background:'var(--pastelRed, #FDF0EF)', borderRadius:12, padding:'14px 22px', margin:'20px auto 0', display:'inline-block' },
  grid: { display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:16, alignItems:'stretch' },
  steps: { display:'flex', flexWrap:'wrap', alignItems:'stretch', justifyContent:'center', gap:16 },
  stepCard: { flex:'1 1 220px', maxWidth:300, background:'#fff', borderRadius:16, padding:'28px 24px', border:'1px solid var(--border)', textAlign:'center', display:'flex', flexDirection:'column', justifyContent:'center' },
  stepHighlight: { background:'var(--brandRed)', color:'#fff', border:'1px solid var(--brandRed)', boxShadow:'0 24px 48px -20px rgba(178,25,45,.55)' },
  stepLabel: { fontSize:12, letterSpacing:'.1em', textTransform:'uppercase', opacity:.75, marginBottom:10 },
  stepBig: { font:'700 44px/1 var(--font-display)', letterSpacing:'-.01em', marginBottom:10 },
  stepSub: { fontSize:14, lineHeight:1.5, opacity:.9 },
  arrow: { alignSelf:'center', font:'700 28px/1 var(--font-display)', color:'var(--grey)' },
  card: {
    background:'var(--bg-cream)', borderRadius:16, padding:'28px 24px 26px',
    border:'1px solid var(--border)',
    display:'flex', flexDirection:'column', position:'relative',
  },
  cardHighlight: {
    background:'var(--brandRed)', color:'#fff', border:'1px solid var(--brandRed)',
    transform:'translateY(-8px)',
    boxShadow:'0 24px 48px -20px rgba(178,25,45,.55)',
  },
  cardHighlightTag: {
    position:'absolute', top:-12, left:20, background:'var(--gold)', color:'var(--fg-1)',
    fontSize:11, fontWeight:700, letterSpacing:'.1em', textTransform:'uppercase',
    padding:'5px 10px', borderRadius:20,
  },
  cardLabel: { fontSize:13, letterSpacing:'.1em', textTransform:'uppercase', opacity:.75, marginBottom:12 },
  cardPrice: { display:'flex', alignItems:'baseline', gap:4, marginBottom:22 },
  cardPriceNum: { font:'700 46px/1 var(--font-display)' },
  cardPriceMo: { fontSize:15, opacity:.75 },
  featList: { listStyle:'none', margin:0, padding:0, display:'flex', flexDirection:'column', gap:10, fontSize:14, lineHeight:1.5 },
  footnote: { fontSize:13, color:'var(--grey)', textAlign:'center', marginTop:20, marginBottom:0 },
  proof: {
    display:'grid', gridTemplateColumns:'auto 1fr', gap:28, alignItems:'center',
    background:'#fff', borderRadius:16, padding:'28px 32px', marginTop:48,
    border:'1px solid var(--border)',
  },
  proofStat: { font:'700 56px/1 var(--font-display)', color:'var(--brandRed)', letterSpacing:'-.02em' },
  proofBody: { font:'400 17px/1.55 var(--font-body)', color:'var(--fg-2)' },
};

export function TeachersSampleAndPricing({ t }) {
  const LANGS = [
    { code:'en-es',   name:'Spanish',    flag:'spanish' },
    { code:'en-fr',   name:'French',     flag:'french' },
    { code:'en-de',   name:'German',     flag:'german' },
    { code:'en-it',   name:'Italian',    flag:'italian' },
    { code:'en-pt',   name:'Portuguese', flag:'portuguese' },
    { code:'en-cmn',   name:'Mandarin',   flag:'chinese' },
    { code:'en-ja',   name:'Japanese',   flag:'japanese' },
    { code:'en-ko',   name:'Korean',     flag:'korean' },
    { code:'en-ar',   name:'Arabic',     flag:'arabic' },
    { code:'en-cy-south', name:'Welsh',      flag:'wales', ext:'png' },
  ];
  const [i, setI] = useState(0);
  const [billing, setBilling] = useState('monthly');
  const [duration, setDuration] = useState(3); // minutes — 3 (taster) | 30 (full session)
  const annual = billing === 'annual';
  const playerRef = useRef(null);

  // Lazy-load the lesson-player script on mount. Idempotent across
  // StrictMode double-mount and multiple consumers (e.g. Sample.jsx
  // also calls this; whichever runs first wins).
  useEffect(() => {
    ensureLessonPlayer();
  }, []);

  useEffect(() => {
    const el = playerRef.current;
    if (!el) return;
    if (typeof el._pause === 'function' && el._playing) el._pause();
    el.setAttribute('course', LANGS[i].code);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i]);

  // Forward duration — player observes the attribute and reloads on change.
  useEffect(() => {
    const el = playerRef.current;
    if (!el) return;
    el.setAttribute('duration', String(duration));
  }, [duration]);

  return (
    <section id="sample" className="ssi-to__sample" style={spStyles.wrap}>
      <div style={spStyles.head}>
        <div style={spStyles.kicker}>{t.toSampleKicker}</div>
        <h2 style={spStyles.h2}>{t.toSampleTitle}</h2>
        <p style={spStyles.lede}>{t.toSampleLede}</p>
      </div>

      <div style={spStyles.pickerWrap}>
        <div style={spStyles.pickerLabel}>{t.toSamplePickerLabel}</div>
        <div style={spStyles.picker}>
          {LANGS.map((l, idx) => {
            const active = idx === i;
            return (
              <button
                key={l.code}
                type="button"
                onClick={()=>setI(idx)}
                style={{
                  ...spStyles.chip,
                  transform: active ? 'translateY(-2px)' : 'none',
                }}>
                <span style={{
                  ...spStyles.chipFlag,
                  boxShadow: active
                    ? '0 0 0 3px var(--brandRed), 0 6px 16px rgba(178,25,45,.28)'
                    : '0 0 0 1px rgba(30,20,18,.08), 0 1px 2px rgba(30,20,18,.06)',
                }}>
                  <img src={`/assets/flag-${l.flag}.${l.ext || 'svg'}`} alt="" style={spStyles.chipFlagImg}/>
                </span>
                <span style={{
                  ...spStyles.chipName,
                  color: active ? 'var(--brandRed)' : 'var(--fg-1)',
                  fontWeight: active ? 600 : 500,
                }}>{l.name}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="ssi-to__sampleCard" style={spStyles.card}>
        {/* Left — Sample player */}
        <div className="ssi-to__sampleLeft" style={spStyles.leftSide}>
          <div style={spStyles.playerWrap}>
            <ssi-lesson-player
              ref={playerRef}
              course={LANGS[i].code}
              duration={String(duration)}
            ></ssi-lesson-player>
          </div>

          {/* Duration toggle — taster vs. full session */}
          <div style={spStyles.durToggle} role="tablist" aria-label="Demo length">
            {[
              { val: 3,  label: '3 min', sub: 'Taster' },
              { val: 30, label: '30 min', sub: 'Full session' },
            ].map(o => {
              const active = duration === o.val;
              return (
                <button
                  key={o.val}
                  role="tab"
                  type="button"
                  aria-selected={active}
                  onClick={() => setDuration(o.val)}
                  style={{
                    ...spStyles.durBtn,
                    background: active ? '#fff' : 'transparent',
                    color: active ? 'var(--fg-1)' : 'var(--grey)',
                    boxShadow: active ? '0 1px 2px rgba(30,20,18,.08), 0 4px 10px rgba(30,20,18,.06)' : 'none',
                  }}>
                  <span style={{fontWeight: active ? 600 : 500}}>{o.label}</span>
                  <span style={spStyles.durSub}>{o.sub}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Right — Pricing */}
        <div className="ssi-to__sampleRight" style={spStyles.rightSide}>
          <div style={spStyles.sideHead}>
            <div style={spStyles.kicker}>{t.toPricingKicker}</div>
            <h3 style={spStyles.sideH}>{t.toPricingTitle}</h3>
            <p style={spStyles.sideLede}>{t.toPricingLede}</p>
          </div>

          <div style={spStyles.toggle}>
            <button onClick={()=>setBilling('monthly')} style={{
              ...spStyles.toggleBtn, ...(!annual ? spStyles.toggleActive : {}),
            }}>{t.toPricingMonthly}</button>
            <button onClick={()=>setBilling('annual')} style={{
              ...spStyles.toggleBtn, ...(annual ? spStyles.toggleActive : {}),
            }}>{t.toPricingAnnual} <span style={spStyles.save}>Save £30</span></button>
          </div>

          <div style={spStyles.priceRow}>
            <span style={spStyles.priceCurr}>£</span>
            <span style={spStyles.priceNum}>{annual ? 150 : 15}</span>
            <span style={spStyles.priceMeta}>/ {annual ? 'year' : 'month'}</span>
          </div>
          <div style={spStyles.effective}>
            {annual ? t.toPricingAnnualEffective : t.toPricingMonthlyEffective}
          </div>

          <ul style={spStyles.feats}>
            <li>{t.toPricingFeat1}</li>
            <li>{t.toPricingFeat2}</li>
            <li>{t.toPricingFeat3}</li>
            <li>{t.toPricingFeat4}</li>
            <li>{t.toPricingFeat5}</li>
          </ul>

          <a href={TEACHER_SETUP_URL} className="btn btn--primary" style={spStyles.cta} id="pricing">
            {t.toPricingCta}
          </a>
          <p style={spStyles.trial}>{t.toPricingTrial}</p>
        </div>
      </div>
    </section>
  );
}

const spStyles = {
  wrap: { background:'var(--bg-cream)', padding:'88px 32px' },
  head: { maxWidth:720, margin:'0 auto 40px', textAlign:'center' },
  h2: { font:'700 44px/1.1 var(--font-display)', margin:'0 0 14px', color:'var(--fg-1)', letterSpacing:'-.01em' },
  lede: { font:'400 17px/1.55 var(--font-body)', color:'var(--fg-2)', margin:0, textWrap:'pretty' },
  pickerWrap: {
    maxWidth:1160, margin:'0 auto 40px', textAlign:'center',
  },
  pickerLabel: {
    fontSize:11, letterSpacing:'.16em', textTransform:'uppercase',
    color:'var(--grey)', fontWeight:600, marginBottom:20,
  },
  card: {
    maxWidth:1160, margin:'0 auto',
    display:'grid', gridTemplateColumns:'1fr 1fr',
    background:'#fff', borderRadius:24, overflow:'hidden',
    border:'1px solid var(--border)',
    boxShadow:'0 1px 2px rgba(0,0,0,.04), 0 20px 50px -25px rgba(73,3,0,.2)',
  },
  leftSide: {
    padding:'44px 40px 40px',
    background:'linear-gradient(180deg, #fff 0%, #faf7f2 100%)',
    borderRight:'1px solid var(--border)',
    display:'flex', flexDirection:'column', justifyContent:'center', alignItems:'center',
  },
  rightSide: {
    padding:'44px 40px 40px',
    display:'flex', flexDirection:'column',
  },
  sideHead: {},
  kicker: { fontSize:12, letterSpacing:'.16em', textTransform:'uppercase', color:'var(--brandRed)', fontWeight:700, marginBottom:12 },
  sideH: { font:'700 30px/1.2 var(--font-display)', margin:'0 0 12px', color:'var(--fg-1)', letterSpacing:'-.01em' },
  sideLede: { font:'400 15px/1.55 var(--font-body)', color:'var(--fg-2)', margin:0 },
  picker: {
    display:'flex', flexWrap:'wrap', justifyContent:'center',
    gap:'18px 20px', marginTop:4,
  },
  chip: {
    background:'transparent', border:'none', padding:0,
    display:'flex', flexDirection:'column', alignItems:'center', gap:6,
    fontFamily:'inherit', cursor:'pointer',
    transition:'transform 160ms ease', minWidth:64,
  },
  chipFlag: {
    width:48, height:48, borderRadius:'50%',
    overflow:'hidden',
    display:'flex', alignItems:'center', justifyContent:'center',
    background:'var(--brandRed)',
    transition:'box-shadow 160ms ease',
  },
  chipFlagImg: { width:'100%', height:'100%', objectFit:'cover' },
  chipName: { fontSize:12, transition:'color 160ms ease' },
  playerWrap: { width:'100%', display:'flex', justifyContent:'center', marginTop:8 },

  durToggle: {
    display:'inline-flex',
    padding:4,
    background:'rgba(30,20,18,.05)',
    borderRadius:999,
    gap:2,
    marginTop:20,
    alignSelf:'center',
  },
  durBtn: {
    border:0,
    padding:'8px 16px',
    borderRadius:999,
    cursor:'pointer',
    fontFamily:'inherit',
    fontSize:13,
    letterSpacing:'-.005em',
    transition:'background 160ms ease, color 160ms ease, box-shadow 160ms ease',
    display:'inline-flex', alignItems:'baseline', gap:6,
  },
  durSub: {
    fontSize:11,
    color:'var(--grey)',
    fontWeight:500,
    letterSpacing:0,
  },

  toggle: {
    display:'inline-flex', alignSelf:'flex-start', background:'var(--bg-cream)', borderRadius:40,
    border:'1px solid var(--border)', padding:4, marginTop:24, marginBottom:20,
  },
  toggleBtn: {
    padding:'8px 16px', borderRadius:30, border:0, background:'transparent',
    cursor:'pointer', font:'inherit', fontSize:13, fontWeight:500, color:'var(--fg-2)',
    display:'inline-flex', alignItems:'center', gap:8,
  },
  toggleActive: { background:'var(--brandRed)', color:'#fff' },
  save: { fontSize:10, background:'var(--gold)', color:'var(--fg-1)', padding:'2px 6px', borderRadius:10, fontWeight:700 },

  priceRow: { display:'flex', alignItems:'baseline', gap:4, marginBottom:4 },
  priceCurr: { font:'700 28px/1 var(--font-display)', color:'var(--fg-1)' },
  priceNum: { font:'700 56px/1 var(--font-display)', color:'var(--fg-1)', letterSpacing:'-.02em' },
  priceMeta: { fontSize:15, color:'var(--grey)', marginLeft:4 },
  effective: { fontSize:13, color:'var(--fg-2)', marginBottom:20 },
  feats: { listStyle:'none', margin:'0 0 24px', padding:0, display:'flex', flexDirection:'column', gap:8, fontSize:14, lineHeight:1.5 },
  cta: { display:'inline-flex', alignItems:'center', justifyContent:'center', width:'100%' },
  trial: { fontSize:12, color:'var(--grey)', margin:'12px 0 0', textAlign:'center', lineHeight:1.5 },
};

/* -------------------------------------------------------------- */

export function TeachersFAQ({ t }) {
  const items = [
    { q: t.toFaqQ1, a: t.toFaqA1 },
    { q: t.toFaqQ2, a: t.toFaqA2 },
    { q: t.toFaqQ3, a: t.toFaqA3 },
    { q: t.toFaqQ4, a: t.toFaqA4 },
    { q: t.toFaqQ5, a: t.toFaqA5 },
    { q: t.toFaqQ6, a: t.toFaqA6 },
    { q: t.toFaqQ7, a: t.toFaqA7 },
    { q: t.toFaqQ8, a: t.toFaqA8 },
    { q: t.toFaqQ9, a: t.toFaqA9 },
  ];
  const [open, setOpen] = useState(0);
  const toggle = (i) => setOpen(open === i ? -1 : i);

  return (
    <section className="ssi-to__faq" style={fqStyles.wrap}>
      <div className="ssi-to__faqInner" style={fqStyles.inner}>
        <aside className="ssi-to__faqSide" style={fqStyles.side}>
          <div style={fqStyles.kicker}>{t.toFaqKicker}</div>
          <h2 style={fqStyles.h2}>{t.toFaqTitle}</h2>
          <p style={fqStyles.lede}>{t.toFaqLede}</p>
          <div style={fqStyles.contact}>
            <div style={fqStyles.contactLabel}>Still unsure?</div>
            <a href="#contact" style={fqStyles.contactLink}>{t.toFaqContact}</a>
          </div>
        </aside>

        <ol style={fqStyles.list}>
          {items.map((it, i) => {
            const isOpen = open === i;
            return (
              <li key={i} style={{
                ...fqStyles.item,
                borderColor: isOpen ? 'var(--brandRed)' : 'var(--border)',
                background: isOpen ? '#fff' : 'transparent',
                boxShadow: isOpen ? '0 8px 24px rgba(178,25,45,.08)' : 'none',
              }}>
                <button
                  type="button"
                  onClick={() => toggle(i)}
                  aria-expanded={isOpen}
                  style={fqStyles.qBtn}>
                  <span style={fqStyles.qNum}>{String(i+1).padStart(2,'0')}</span>
                  <span style={fqStyles.qText}>{it.q}</span>
                  <span style={{
                    ...fqStyles.qIcon,
                    transform: isOpen ? 'rotate(45deg)' : 'rotate(0deg)',
                    color: isOpen ? 'var(--brandRed)' : 'var(--grey)',
                  }} aria-hidden>+</span>
                </button>
                <div style={{
                  ...fqStyles.aWrap,
                  maxHeight: isOpen ? 500 : 0,
                  opacity: isOpen ? 1 : 0,
                }}>
                  <p style={fqStyles.aText}>{it.a}</p>
                </div>
              </li>
            );
          })}
        </ol>
      </div>
    </section>
  );
}

const fqStyles = {
  wrap: { background:'var(--offWhite, #FDF5F5)', padding:'104px 32px 96px' },
  inner: {
    maxWidth:1200, margin:'0 auto',
    display:'grid', gridTemplateColumns:'340px 1fr', gap:64, alignItems:'start',
  },
  side: { position:'sticky', top:40 },
  kicker: { fontSize:12, letterSpacing:'.14em', textTransform:'uppercase', color:'var(--brandRed)', fontWeight:600, marginBottom:12 },
  h2: { fontFamily:'var(--font-display)', fontSize:44, lineHeight:1.05, margin:'0 0 14px', letterSpacing:'-.015em' },
  lede: { fontSize:16, color:'var(--grey)', lineHeight:1.6, margin:'0 0 28px', textWrap:'pretty' },
  contact: { borderTop:'1px solid var(--border)', paddingTop:20 },
  contactLabel: { fontSize:13, color:'var(--grey)', marginBottom:6 },
  contactLink: { fontSize:16, color:'var(--brandRed)', fontWeight:600, textDecoration:'none' },
  list: { listStyle:'none', margin:0, padding:0, display:'flex', flexDirection:'column', gap:12 },
  item: {
    border:'1px solid var(--border)', borderRadius:14,
    transition:'border-color 160ms ease, background 160ms ease, box-shadow 160ms ease',
    overflow:'hidden',
  },
  qBtn: {
    display:'grid', gridTemplateColumns:'auto 1fr auto',
    alignItems:'center', gap:20,
    width:'100%', padding:'22px 26px',
    background:'transparent', border:0, cursor:'pointer', textAlign:'left',
    font:'inherit', color:'inherit',
  },
  qNum: { fontFamily:'var(--font-display)', fontSize:14, color:'var(--brandRed)', opacity:.7, letterSpacing:'.05em' },
  qText: { fontFamily:'var(--font-display)', fontSize:20, lineHeight:1.3, color:'var(--fg-1)', letterSpacing:'-.005em', textWrap:'balance' },
  qIcon: { fontSize:26, fontWeight:300, width:28, textAlign:'center', transition:'transform 200ms ease, color 160ms ease' },
  aWrap: { overflow:'hidden', transition:'max-height 260ms ease, opacity 200ms ease' },
  aText: { margin:0, padding:'0 26px 26px 82px', fontSize:16, lineHeight:1.65, color:'var(--grey)', textWrap:'pretty' },
};

/* -------------------------------------------------------------- */

export function FooterCTA({ t }) {
  const [billing, setBilling] = useState('monthly');
  const annual = billing === 'annual';
  return (
    <section className="ssi-to__footerCta" style={fcStyles.wrap}>
      <div style={fcStyles.inner}>
        <h2 style={fcStyles.h2}>{t.toFooterCtaTitle}</h2>
        <p style={fcStyles.sub}>{t.toFooterCtaSub}</p>

        <div style={fcStyles.card}>
          <div style={fcStyles.toggle}>
            <button onClick={()=>setBilling('monthly')} style={{
              ...fcStyles.toggleBtn, ...(!annual ? fcStyles.toggleActive : {}),
            }}>{t.toPricingMonthly}</button>
            <button onClick={()=>setBilling('annual')} style={{
              ...fcStyles.toggleBtn, ...(annual ? fcStyles.toggleActive : {}),
            }}>
              {t.toPricingAnnual}
              <span style={fcStyles.save}>Save £30</span>
            </button>
          </div>

          <div style={fcStyles.priceRow}>
            <span style={fcStyles.priceCurr}>£</span>
            <span style={fcStyles.priceNum}>{annual ? 150 : 15}</span>
            <span style={fcStyles.priceMeta}>/ {annual ? 'year' : 'month'}</span>
          </div>
          <div style={fcStyles.effective}>
            {annual ? t.toPricingAnnualEffective : t.toPricingMonthlyEffective}
          </div>

          <a href={TEACHER_SETUP_URL} style={fcStyles.cta}>
            {t.toFooterCtaBtn}
          </a>
          <p style={fcStyles.trial}>{t.toPricingTrial}</p>
        </div>
      </div>
    </section>
  );
}

const fcStyles = {
  wrap: { background:'var(--brandRed)', color:'#fff', padding:'96px 32px', textAlign:'center' },
  inner: { maxWidth:640, margin:'0 auto' },
  h2: { font:'700 52px/1.1 var(--font-display)', margin:'0 0 20px', letterSpacing:'-.01em', color:'#fff' },
  sub: { font:'400 19px/1.55 var(--font-body)', margin:'0 0 36px', opacity:.92, color:'#fff' },
  card: {
    background:'#fff', borderRadius:20, padding:'28px 32px 26px',
    boxShadow:'0 20px 60px -20px rgba(0,0,0,.4)',
    textAlign:'center', color:'var(--fg-1)',
  },
  toggle: {
    display:'inline-flex', background:'var(--bg-cream)', borderRadius:40,
    border:'1px solid var(--border)', padding:4, marginBottom:20,
  },
  toggleBtn: {
    padding:'8px 16px', borderRadius:30, border:0, background:'transparent',
    cursor:'pointer', font:'inherit', fontSize:13, fontWeight:500, color:'var(--fg-2)',
    display:'inline-flex', alignItems:'center', gap:8,
  },
  toggleActive: { background:'var(--brandRed)', color:'#fff' },
  save: { fontSize:10, background:'var(--gold)', color:'var(--fg-1)', padding:'2px 6px', borderRadius:10, fontWeight:700 },

  priceRow: { display:'flex', alignItems:'baseline', justifyContent:'center', gap:4, marginBottom:4 },
  priceCurr: { font:'700 26px/1 var(--font-display)', color:'var(--fg-1)' },
  priceNum: { font:'700 56px/1 var(--font-display)', color:'var(--fg-1)', letterSpacing:'-.02em' },
  priceMeta: { fontSize:15, color:'var(--grey)', marginLeft:4 },
  effective: { fontSize:13, color:'var(--fg-2)', marginBottom:20 },

  cta: {
    display:'inline-flex', alignItems:'center', justifyContent:'center',
    width:'100%', padding:'14px 20px', borderRadius:30,
    background:'var(--brandRed)', color:'#fff',
    fontSize:16, fontWeight:600, textDecoration:'none',
    letterSpacing:'.01em',
  },
  trial: { fontSize:12, color:'var(--grey)', margin:'14px 0 0', lineHeight:1.5 },
};
